import { BrokerAuthPage } from "Auth/Pages/BrokerAuthPage";
import { SignInPage } from "Auth/Pages/SignInPage";
import { DashboardPage } from "Dashboard/Pages/DashboardPage";
import { Error403Page } from "Infrastructure/Pages/Error403Page";
import { Error404Page } from "Infrastructure/Pages/Error404Page";
import { AuthenticatedRoute } from "Infrastructure/Routing/AuthenticatedRoute";
import { UnauthenticatedRoute } from "Infrastructure/Routing/UnauthenticatedRoute";
import { AppRouting, getRoute } from "Infrastructure/Utils/UrlUtils";
import { PublicLayout } from "Shared/Layouts/PublicLayout";
import { createBrowserRouter } from "react-router";

// Authenticated heavy pages — lazy so they don't ship in the initial bundle.
// PdfBuilderPage pulls in `react-pdf` (~300 KB), PathTemplate/FormBuilder pull
// in `@dnd-kit/*`, and the Module/PathRun pages pull in `recharts` plus all 28
// module bodies via `ModuleRegistry`. Sign-in / Dashboard stay eager — they're
// the most common entry points.
//
// The named `preload*` factories are exported so the topbar / drawer nav can
// kick off the chunk download on hover/focus (see AuthenticatedLayout +
// NavButton). Vite/the browser de-dupes repeat calls to `import(...)` of the
// same module specifier, so calling these multiple times is free.
export const preloadModuleLibrary = () =>
	import("Modeling/Pages/ModuleLibraryPage");
export const preloadStandaloneModule = () =>
	import("Modeling/Pages/StandaloneModulePage");
export const preloadPathRunDraft = () =>
	import("Modeling/Pages/PathRunDraftPage");
export const preloadPathRunDetail = () =>
	import("Modeling/Pages/PathRunDetailPage");
export const preloadPathRunList = () =>
	import("Modeling/Pages/PathRunListPage");
export const preloadPathRunStepDetail = () =>
	import("Modeling/Pages/PathRunStepDetailPage");
export const preloadPathTemplateBuilder = () =>
	import("Modeling/Pages/PathTemplateBuilderPage");
export const preloadPathTemplateLibrary = () =>
	import("Modeling/Pages/PathTemplateLibraryPage");
export const preloadPdfBuilder = () => import("Modeling/Pages/PdfBuilderPage");
export const preloadFormBuilder = () =>
	import("Modeling/Pages/FormBuilderPage");
export const preloadPublicPathShare = () =>
	import("Modeling/Pages/PublicPathSharePage");
export const preloadPublicModuleShare = () =>
	import("Modeling/Pages/PublicModuleSharePage");
export const preloadPrintModule = () =>
	import("Modeling/Pages/PrintModulePage");
export const preloadPrintPath = () => import("Modeling/Pages/PrintPathPage");

const appRoutes = [
	{
		element: <UnauthenticatedRoute />,
		children: [
			{ path: getRoute(AppRouting.SignIn), element: <SignInPage /> },
			{ path: getRoute(AppRouting.BrokerAuth), element: <BrokerAuthPage /> },
		],
	},
	{
		element: <AuthenticatedRoute />,
		children: [
			{ path: getRoute(AppRouting.Dashboard), element: <DashboardPage /> },
			{
				path: getRoute(AppRouting.ModuleLibrary),
				lazy: async () => {
					const m = await preloadModuleLibrary();
					return { Component: m.ModuleLibraryPage };
				},
			},
			{
				path: getRoute(AppRouting.PdfBuilderNew),
				lazy: async () => {
					const m = await preloadPdfBuilder();
					return { Component: () => <m.PdfBuilderPage mode="new" /> };
				},
			},
			{
				path: getRoute(AppRouting.PdfBuilderEdit),
				lazy: async () => {
					const m = await preloadPdfBuilder();
					return { Component: () => <m.PdfBuilderPage mode="edit" /> };
				},
			},
			{
				path: getRoute(AppRouting.FormBuilderNew),
				lazy: async () => {
					const m = await preloadFormBuilder();
					return { Component: () => <m.FormBuilderPage mode="new" /> };
				},
			},
			{
				path: getRoute(AppRouting.FormBuilderEdit),
				lazy: async () => {
					const m = await preloadFormBuilder();
					return { Component: () => <m.FormBuilderPage mode="edit" /> };
				},
			},
			{
				path: getRoute(AppRouting.ModuleStandalone),
				lazy: async () => {
					const m = await preloadStandaloneModule();
					return { Component: m.StandaloneModulePage };
				},
			},
			{
				path: getRoute(AppRouting.PathTemplateLibrary),
				lazy: async () => {
					const m = await preloadPathTemplateLibrary();
					return { Component: m.PathTemplateLibraryPage };
				},
			},
			{
				path: getRoute(AppRouting.PathTemplateNew),
				lazy: async () => {
					const m = await preloadPathTemplateBuilder();
					return { Component: () => <m.PathTemplateBuilderPage mode="new" /> };
				},
			},
			{
				path: getRoute(AppRouting.PathTemplateEdit),
				lazy: async () => {
					const m = await preloadPathTemplateBuilder();
					return { Component: () => <m.PathTemplateBuilderPage mode="edit" /> };
				},
			},
			{
				path: getRoute(AppRouting.PathTemplateCopy),
				lazy: async () => {
					const m = await preloadPathTemplateBuilder();
					return { Component: () => <m.PathTemplateBuilderPage mode="copy" /> };
				},
			},
			{
				path: getRoute(AppRouting.PathRunList),
				lazy: async () => {
					const m = await preloadPathRunList();
					return { Component: m.PathRunListPage };
				},
			},
			{
				path: getRoute(AppRouting.PathRunDetail),
				lazy: async () => {
					const m = await preloadPathRunDetail();
					return { Component: m.PathRunDetailPage };
				},
			},
			{
				// Sentinel route — declared before the parametric PathRun routes
				// so react-router matches "draft" literally instead of treating
				// it as a PathRunID. (React-router scores static segments higher,
				// but explicit ordering keeps the intent obvious to readers.)
				path: getRoute(AppRouting.PathRunDraftStep),
				lazy: async () => {
					const m = await preloadPathRunDraft();
					return { Component: m.PathRunDraftPage };
				},
			},
			{
				path: getRoute(AppRouting.PathRunStepDetail),
				lazy: async () => {
					const m = await preloadPathRunStepDetail();
					return { Component: m.PathRunStepDetailPage };
				},
			},
		],
	},
	{
		// Public, anonymous client-share views — NOT wrapped by AuthenticatedRoute
		// (no userinfo probe, no token required). Minimal tenant-branded layout.
		element: <PublicLayout />,
		children: [
			{
				path: getRoute(AppRouting.PublicPathShare),
				lazy: async () => {
					const m = await preloadPublicPathShare();
					return { Component: m.PublicPathSharePage };
				},
			},
			{
				path: getRoute(AppRouting.PublicModuleShare),
				lazy: async () => {
					const m = await preloadPublicModuleShare();
					return { Component: m.PublicModuleSharePage };
				},
			},
			{
				path: getRoute(AppRouting.PrintModule),
				lazy: async () => {
					const m = await preloadPrintModule();
					return { Component: m.PrintModulePage };
				},
			},
			{
				path: getRoute(AppRouting.PrintPath),
				lazy: async () => {
					const m = await preloadPrintPath();
					return { Component: m.PrintPathPage };
				},
			},
		],
	},
	{ path: getRoute(AppRouting.Error403), element: <Error403Page /> },
	{ path: "*", element: <Error404Page /> },
];

export const appRouter = createBrowserRouter(appRoutes, {
	basename: import.meta.env.VITE_BASE_URL,
});
